import React, { useState, useEffect, useRef } from 'react'
import validator from 'validator'
import Check from '../../../assets/thank-you/green-check.png';
import isStrongPassword from 'validator/lib/isStrongPassword';
import isLength from 'validator/lib/isLength';
import { Link } from 'react-router-dom';
import { Card, Button, Form, Alert, Spinner } from 'react-bootstrap'
import { useRealmApp } from '../../RealmApp'
// import { CopyToClipboard } from 'react-copy-to-clipboard'
import { FaHandPointRight } from 'react-icons/fa';
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai'
import { IoMdClose } from 'react-icons/io';
import { BiAnalyse } from 'react-icons/bi';
import TagManager from 'react-gtm-module';
import './thank-you.css'
import { useSupabaseApp } from '../../SupabaseApp';
import { useLocation } from 'react-router-dom';
import { gtagSendEvent } from "../../utils/google";

/**
 * ThankYouPage Component
 * 
 * Post-purchase thank you page displayed after Stripe checkout session completion.
 * Extracts purchase details from URL parameters including session ID, customer email, 
 * purchase amount, and Google Ads tracking parameters. Sends analytics event to Google
 * Analytics and forces user logout (requiring user to log in again to refresh tier data).
 * 
 * Note: MongoDB Realm requires users to re-authenticate to fetch updated custom data/tier
 * information from newly completed purchases.
 * 
 * @returns {JSX.Element} Thank you page with payment confirmation and instructions
 */
function ThankYouPage() {

    const { logOut } = useSupabaseApp();

    // Get URL search parameters for purchase metadata
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);

    // Extract Stripe session ID from URL parameters
    const sessionId = searchParams.get('session_id');

    // Extract Google Ads tracking ID (gclid) from URL parameters
    const gclid = searchParams.get('gclid');

    // Extract purchase type from URL parameters
    const purchase = searchParams.get('purchase');

    // Extract customer email from URL parameters and normalize to lowercase
    let customerEmail = searchParams.get('customer_email');
    if (customerEmail) customerEmail = customerEmail.toLowerCase().trim();

    // Extract the purchase amount paid from URL parameters
    const purchasePaid = searchParams.get('purchase_paid');

    // Extract livemode flag indicating whether this is production or test payment
    const livemode = searchParams.get('livemode');

    // Prepare analytics event data with purchase details
    const target = {
        "eventName": "purchase_complete",
        "sessionId": sessionId,
        "purchasePaid": parseFloat(purchasePaid),
        "customerEmail": customerEmail,
        "livemode": parseInt(livemode)
    };

    // Send purchase completion event to Google Analytics
    gtagSendEvent(target);

    /**
     * Force logout user on component mount
     * Users must re-authenticate to refresh their tier/subscription data in Realm
     * MongoDB Realm does not dynamically update custom data when underlying documents change
     */
    useEffect(() => {
        // Declare async logout function
        const forceLogout = async () => {
            try {
                const response = await logOut();
                // Handle logout response
            } catch (error) {
                // Handle logout error silently
            }
        };

        // Execute logout on component mount
        forceLogout();

        // Cleanup function (empty)
        return () => {
        };

    }, []);

    return (
        <div id="thankyoucontainer">
            <div id="thankyouloginform" style={{ padding: "80px 0" }}>
                <Card className="thankyoucard">
                    <div style={{ textAlign: "center" }}>
                        <div style={{ paddingBottom: "40px" }}>
                            <img src={Check} alt="" style={{ width: "120px", height: "120px" }} />
                        </div>

                        <p className="thankyouheader">Thank you for your payment and support!</p>

                        <div className="thankyoutext">
                            <p>We've securely completed your payment and you'll receive a confirmation email shortly.</p>
                        </div>

                        <div className="thankyoutext">
                            <p>You may access the <strong>Billing Portal</strong> via the dashboard to view the information about your subscription plan, service charge details, and billing history, as well as to download/print your invoice and receipt.</p>
                        </div>

                        <div className="thankyoutext">
                            <p>We've logged you out to ensure that your account is upgraded. If your plan is not successfully upgraded, please contact our Support Team via the <a href="https://www.roiquant.com/contact">chat widget or submit an inquiry</a>.</p>
                        </div>

                        <div className="thankyoutext">
                            <p><u>Note</u>: If you haven't verified your newly created roiquant account from the verification email we sent you, you won't be able to log into our roiquant web-app even after you've purchased our subscription plan.</p>

                        </div>

                        <div className="thankyoutext">
                            <p>If you can't find your verification email, you may click on <strong>'Still can't log in?'</strong> on our login pages to request a new verification email to activate your new account. Once verified, you'll be able to log in.</p>
                        </div>

                        <div className="thankyoubutton">
                            <a href="/dashboard">
                                <Button className="thankyoudashboardbutton glintanimation">Log into dashboard</Button>
                            </a>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    )
}

export default ThankYouPage
